import { provisionShardedDatabaseBlockDataSource } from '@blocksuite/affine-block-database';
import type { Store } from '@blocksuite/store';
import {
  classifyDatabaseRole,
  CUSTOM_APP_VIEW_CONFIG_DATABASE_LAYOUT,
  getViewConfigColumnId,
  serializeCustomAppDatabaseXYWH,
  SL_VIEW_ROLE_KEY,
  SL_VIEW_ROLE_VALUES,
  VIEW_CONFIG_COLUMN_DEFS,
  type ViewConfigColumnDef,
} from '@streamlain/view-config';
import { Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { Text as YText } from 'yjs';

export interface ViewConfigProvisionResult {
  databaseBlockId: string;
  columnMap: Record<string, string>;
}

const MANAGED_BY = 'view-config';
const DB_TITLE = 'View Configuration';

function buildColumn(def: ViewConfigColumnDef): {
  id: string;
  type: string;
  name: string;
  data: Record<string, unknown>;
} {
  return {
    id: getViewConfigColumnId(def.field),
    type: def.blockSuiteType,
    name: def.field,
    data: {},
  };
}

export class ViewConfigDatabaseService extends Service {
  provision(doc: Store, databaseId?: string): ViewConfigProvisionResult {
    if (!doc?.root) {
      throw new Error(
        'ViewConfigDatabaseService.provision: doc.root is undefined'
      );
    }

    // 'viewId' is derived from the child block id; 'name' is stored as the
    // paragraph text. Neither is a persisted database column.
    const defs = VIEW_CONFIG_COLUMN_DEFS.filter(
      d => d.field !== 'name' && d.field !== 'viewId'
    );
    const columns = defs.map(buildColumn);
    const columnMap: Record<string, string> = {};
    defs.forEach((d, i) => {
      const c = columns[i];
      if (c) columnMap[d.field] = c.id;
    });

    const provisioned = provisionShardedDatabaseBlockDataSource(doc, {
      databaseId,
      title: DB_TITLE,
      props: {
        xywh: serializeCustomAppDatabaseXYWH(
          CUSTOM_APP_VIEW_CONFIG_DATABASE_LAYOUT
        ),
        index: CUSTOM_APP_VIEW_CONFIG_DATABASE_LAYOUT.index,
        lockedBySelf: false,
        schemaLocked: true,
        managedBy: MANAGED_BY,
        [SL_VIEW_ROLE_KEY]: SL_VIEW_ROLE_VALUES.CONFIG,
      },
      initDataSource: dataSource => {
        const storageModel = dataSource.storageModel as any;
        storageModel.store.transact(() => {
          storageModel.props.schemaLocked = true;
          storageModel.props.managedBy = MANAGED_BY;
          storageModel.props[SL_VIEW_ROLE_KEY] = SL_VIEW_ROLE_VALUES.CONFIG;
          storageModel.props.columns = columns;
          storageModel.props.views.splice(0, storageModel.props.views.length, {
            id: nanoid(),
            name: 'Table View',
            mode: 'table',
            columns: [
              { id: 'title', hide: false, width: 260 },
              ...columns.map(col => ({
                id: col.id,
                hide: false,
                width: 180,
              })),
            ],
            filter: { type: 'group', op: 'and', conditions: [] },
            header: { titleColumn: 'title', iconColumn: 'type' },
          });

          const titleText = new YText();
          titleText.insert(0, DB_TITLE);
          storageModel.props.title = titleText;
        });
      },
    });
    const databaseBlockId = provisioned.databaseBlockId;

    const dbModel = provisioned.model;
    doc.updateBlock(dbModel, {
      schemaLocked: true,
      managedBy: MANAGED_BY,
    });

    (dbModel as any).store.transact(() => {
      (dbModel as any).props.schemaLocked = true;
      (dbModel as any).props.managedBy = MANAGED_BY;
      (dbModel as any).props[SL_VIEW_ROLE_KEY] = SL_VIEW_ROLE_VALUES.CONFIG;
    });

    return { databaseBlockId, columnMap };
  }

  findDatabaseBlockId(doc: any): string | null {
    if (!doc?.root) return null;
    const yDoc = doc.spaceDoc ?? doc.yDoc;
    if (!yDoc) return null;
    const blocks = yDoc.getMap('blocks');
    if (!blocks) return null;

    let found: string | null = null;
    blocks.forEach((blockMap: any, blockId: string) => {
      if (found) return;
      const flavour = blockMap?.get?.('sys:flavour');
      if (flavour !== 'affine:database') return;
      const modelProps = doc.getBlock?.(blockId)?.model?.props as
        | Record<string, unknown>
        | undefined;
      const role = classifyDatabaseRole(
        key =>
          blockMap?.get?.(`prop:${key}`) ??
          blockMap?.get?.(key) ??
          modelProps?.[key]
      );
      if (role === 'config') found = blockId;
    });
    return found;
  }
}
